// src/pages/ProductListPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LocationContext } from '../contexts/LocationContext';
import { CartContext } from '../contexts/CartContext';
import Header from '../components/layout/Header';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ApiService from '../services/ApiService';
import ImageService from '../services/ImageService';
import ProductImage from '../components/ui/ProductImage';

// Importer la nouvelle feuille de style
import '../styles/product-list.css';
import '../styles/product-modal-animations.css'; // ✅ AJOUT : Animations pour la modal

const ProductListPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { 
    userLocation, 
    distributorId, 
    nearestDistributor, 
    loading: locationLoading, 
    backgroundUpdate 
  } = useContext(LocationContext);
  const { 
	  addToCart, 
	  removeFromCart, 
	  getItemQuantity, 
	  getTotalItems, 
	  calculateTotal, 
	  getRemainingAmount,
	  isMinimumReached
	} = useContext(CartContext);
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [cartClearedMessage, setCartClearedMessage] = useState(false);
  
  const [stockToast, setStockToast] = useState({
	  show: false,
	  productId: null,
	  message: ''
  });

  // Configuration des catégories avec leurs routes et états
  const categories = [
    { id: 'eaux', label: 'Eaux', hasProducts: true },
    { id: 'sodas', label: 'Boissons gazeuses', hasProducts: true },
    { id: 'bieres', label: 'Bières et Alcools-Mix', hasProducts: true }, // AJOUTÉ : Catégorie bières
  //  { id: 'beer', label: 'Bière', hasProducts: false, route: '/beer' },
  //  { id: 'draft-beer', label: 'Bières à Pression', hasProducts: false, route: '/beers' },
  //  { id: 'wine', label: 'Vins', hasProducts: false, route: '/wine' },
  //  { id: 'spirits', label: 'Spiritueux', hasProducts: false, route: '/spirits' }
  ];

  // AJOUT : Sauvegarder la catégorie courante dans le localStorage
  useEffect(() => {
    if (category) {
      localStorage.setItem('lastVisitedCategory', category);
      console.log('Catégorie sauvegardée:', category);
    }
  }, [category]);

  // Titre de la catégorie
  const getCategoryTitle = () => {
    const currentCategory = categories.find(cat => cat.id === category);
    return currentCategory ? currentCategory.label : 'Produits';
  };
  
  // Vérifier si le panier a été vidé
  useEffect(() => {
    const cartCleared = sessionStorage.getItem('cartCleared');
    if (cartCleared) {
      setCartClearedMessage(true);
      sessionStorage.removeItem('cartCleared');
      
      // Masquer le message après 5 secondes
      setTimeout(() => {
        setCartClearedMessage(false);
      }, 5000);
    }
  }, []);

  // Charger les produits de la catégorie
  useEffect(() => {
    const fetchProducts = async () => {
      // Attendre que le LocationContext soit initialisé
      if (locationLoading) {
        console.log("⏳ Attente de l'initialisation du LocationContext...");
        return;
      }

      // Vérifier que nous avons un distributeur
      if (!distributorId) {
        setError('Distributeur non trouvé');
        setLoading(false);
        return;
      }

      // Vérifier si la catégorie a des produits
      const currentCategory = categories.find(cat => cat.id === category);
      if (currentCategory && !currentCategory.hasProducts) {
        // Rediriger vers la page "Bientôt disponible"
        navigate(currentCategory.route);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log(`🛒 Chargement des produits pour la catégorie "${category}" du distributeur #${distributorId}`);
        
        const response = await ApiService.fetchProduitsDistributeur(distributorId);
        
        // Fonction utilitaire pour normaliser les chaînes de caractères
        const normalizeString = (str) => {
          return str ? str.trim().toLowerCase() : '';
        };
        
        // Fonction pour vérifier si un type est présent (gère les champs MySQL SET avec valeurs multiples)
        const hasType = (typeField, searchType) => {
          if (!typeField) return false;
          
          // Normaliser le champ type_produit (peut contenir plusieurs valeurs séparées par virgules)
          const types = normalizeString(typeField).split(',').map(t => t.trim());
          const searchTypeNormalized = normalizeString(searchType);
          
          // Vérifier si le type recherché est présent dans la liste
          return types.includes(searchTypeNormalized);
        };
        
        // Filtrer les produits par catégorie en utilisant le champ type_produit
        let filteredProducts = [];
        
        if (category === 'eaux') {
          // Filtrer STRICTEMENT les eaux - UNIQUEMENT par type_produit
          filteredProducts = response.filter(p => hasType(p.type_produit, 'eau'));
        } else if (category === 'sodas') {
          // Filtrer STRICTEMENT les boissons gazeuses - UNIQUEMENT par type_produit
          filteredProducts = response.filter(p => 
            hasType(p.type_produit, 'bg') || hasType(p.type_produit, 'boisson gazeuse')
          );
        } else if (category === 'bieres') {
          // Filtrer STRICTEMENT les bières - UNIQUEMENT par type_produit
          filteredProducts = response.filter(p => 
            hasType(p.type_produit, 'biere') || hasType(p.type_produit, 'bière')
          );
        } else if (category === 'autres') {
          // Filtrer les autres boissons qui n'ont pas de type_produit défini ou qui ont un autre type
          filteredProducts = response.filter(p => 
            !hasType(p.type_produit, 'eau') && 
            !hasType(p.type_produit, 'bg') && 
            !hasType(p.type_produit, 'boisson gazeuse') && 
            !hasType(p.type_produit, 'biere') && 
            !hasType(p.type_produit, 'bière')
          );
        }
        
        console.log(`📊 Filtrage pour "${category}":`, {
          totalProduits: response.length,
          produitsFiltrés: filteredProducts.length,
          types: [...new Set(response.map(p => p.type_produit))],
          echantillon: filteredProducts.slice(0, 3).map(p => ({ nom: p.nom, type: p.type_produit }))
        });
        
        // Mapper les produits pour le format d'affichage
        const formattedProducts = filteredProducts.map(product => {
          // ✅ MODIFICATION : Afficher les bouteilles au lieu des packs dans la liste
		  const bottleImageUrl = ImageService.getBottleImageUrl(product.code_produit || product.code || '');
		  const packImageUrl = ImageService.getPackImageUrl(product.code_produit || product.code || '');

		  // Garder aussi les URLs avec fallbacks pour compatibilité modale
		  const imageUrls = ImageService.getProductImageUrls(product, 'large');
		  const thumbUrls = ImageService.getProductImageUrls(product, 'small');
          
          // Vérifier et formater le prix avec une vérification plus robuste
          let productPrice = 0;
          if (typeof product.prix_pack === 'number') {
            productPrice = product.prix_pack;
          } else if (typeof product.prix === 'number') {
            productPrice = product.prix;
          }
          
          return {
            id: product.id_produit,
            displayName: product.nom,
            code: product.code,
			code_produit: product.code_produit, // ✅ IMPORTANT pour ImageService
			bouteilles_par_pack: product.bouteilles_par_pack || 0, // ✅ IMPORTANT pour détecter les bidons
            // URL d'images avec fallbacks
            displayImage: bottleImageUrl, // ✅ Afficher la bouteille au lieu du pack
			packImage: packImageUrl, // ✅ Conserver l'image du pack pour la modal
            displayImageFallbacks: imageUrls.fallbacks,
            thumbnailImage: thumbUrls.primary,
            thumbnailImageFallbacks: thumbUrls.fallbacks,
            price: productPrice,
            bouteilles_par_pack: product.bouteilles_par_pack || 0, // ✅ CORRECTION : Champ manquant ajouté
            packaging: `Pack de ${product.bouteilles_par_pack || 0} ${
    product.conditionnement === 'canette' ? 'canettes' : 
    product.conditionnement === 'bidon' ? 'bidons' : 'bouteilles'
  }`,
            stock: product.quantite_stock || 0,
            description: product.description || '',
			conditionnement: product.conditionnement || 'bouteille_plastique' // ✅ AJOUT
          };
        });
        
        console.log(`✅ ${formattedProducts.length} produits chargés pour la catégorie "${category}"`);
        setProducts(formattedProducts);
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Erreur lors du chargement des produits:', error);
        setError('Impossible de charger les produits');
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, distributorId, locationLoading]);

  const handleCategoryChange = (newCategory) => {
    const selectedCategory = categories.find(cat => cat.id === newCategory);
    
    if (selectedCategory && !selectedCategory.hasProducts) {
      // Si la catégorie n'a pas de produits, naviguer vers sa page "Bientôt disponible"
      navigate(selectedCategory.route);
    } else {
      // Sinon, naviguer vers la liste des produits
      navigate(`/products/${newCategory}`);
    }
  };
  
  // Fonction pour gérer l'ajout au panier avec validation de stock
	const handleAddToCart = (product) => {
	  const result = addToCart(product);
	  
	  if (!result.success && result.reason === 'stock_max') {
		// Afficher le toast au-dessus du produit
		setStockToast({
		  show: true,
		  productId: product.id,
		  message: 'Quantité maximale atteinte'
		});
		
		// Masquer automatiquement après 3 secondes
		setTimeout(() => {
		  setStockToast({ show: false, productId: null, message: '' });
		}, 3000);
	  }
	};

  const goToCart = () => {
    navigate('/cart');
  };
  
  // Fonction pour ouvrir le modal avec le produit sélectionné
  const handleProductImageClick = (product) => {
    console.log('Produit cliqué:', product);
    console.log('Code produit:', product.code);
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Fonction pour fermer le modal
  const closeModal = () => {
    setIsModalOpen(false);
    // Attendre la fin de l'animation avant de réinitialiser le produit
    setTimeout(() => {
      setSelectedProduct(null);
    }, 300);
  };
  
  // Empêcher le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Afficher le loading si le LocationContext est encore en cours de chargement
  if (locationLoading || loading) {
    return (
      <div className="product-list-page">
        <Header title={getCategoryTitle()} showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
        <div className="loading-container">
          <LoadingSpinner />
          <p style={{ marginTop: '1rem', color: '#666' }}>
            {locationLoading ? 'Chargement du distributeur...' : 'Chargement des produits...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-list-page">
        <Header title={getCategoryTitle()} showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
        <div className="loading-container">
          <div className="text-center">
            <p style={{ color: '#ff4966', marginBottom: '1rem' }}>{error}</p>
            <button 
              onClick={() => window.location.reload()}
              style={{ backgroundColor: '#ff4966', color: 'white', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none' }}
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-list-page">
      <Header title={getCategoryTitle()} showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
	  
	  {/* Message d'information sur le vidage du panier */}
      {cartClearedMessage && (
        <div style={{
          background: '#fff3cd',
          color: '#856404',
          padding: '1rem',
          margin: '0.5rem 1rem',
          borderRadius: '8px',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          animation: 'slideDown 0.3s ease-out'
        }}>
          ⚠️ Le distributeur a changé. Votre panier a été vidé car les produits varient selon les distributeurs.
        </div>
      )}
      
      {/* Indicateur de mise à jour en arrière-plan */}
      {backgroundUpdate && (
        <div style={{
          position: 'fixed',
          top: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255, 73, 102, 0.9)',
          color: 'white',
          padding: '0.5rem 1rem',
          borderRadius: '20px',
          fontSize: '0.8rem',
          zIndex: 1000
        }}>
          🔄 Mise à jour de la position...
        </div>
      )}
      
      {/* Onglets de catégories avec support du scroll horizontal */}
		<div className="category-tabs-wrapper">
		  <div className="category-tabs">
			{categories.map((cat) => (
			  <button 
				key={cat.id}
				onClick={() => handleCategoryChange(cat.id)}
				className={`category-tab tab-${cat.id} ${category === cat.id ? 'active' : ''} ${!cat.hasProducts ? 'coming-soon' : ''}`}
			  >
				{cat.label}
				{!cat.hasProducts && <span className="soon-badge">Bientôt</span>}
			  </button>
			))}
		  </div>
		</div>
      
      {/* Informations du distributeur */}
      {/* {nearestDistributor && (
        <div style={{
          background: '#f8f9fa',
          padding: '0.75rem',
          margin: '0 1rem',
          borderRadius: '8px',
          fontSize: '0.8rem',
          color: '#666',
          display: 'flex',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          📍 {nearestDistributor.nom}
          {nearestDistributor.distance !== undefined && (
            <span style={{ marginLeft: '0.5rem' }}>
              • {nearestDistributor.distance.toFixed(1)} km
            </span>
          )}
        </div>
      )} */}
      
      {/* Liste des produits */}
      <div className="product-list-container">
        {products.length === 0 ? (
          <div className="no-products-message">
            Aucun produit disponible dans cette catégorie
          </div>
        ) : (
          <div className="product-list">
            {products.map(product => (
              <div key={product.id} className="product-item">
                <div 
				  className="product-image-container"
				  onClick={() => handleProductImageClick(product)}
				  style={{ cursor: 'pointer', position: 'relative' }}
				>
				  <img
					src={product.displayImage || product.thumbnailImage}
					alt={product.displayName}
					className="product-image"
				  />
				  
				  {/* ✅ NOUVEAU : Badge de stock */}
				  {(() => {
					const currentQty = getItemQuantity(product.id);
					const stockRemaining = product.stock - currentQty;
					
					// Badge rouge si stock max atteint
					if (stockRemaining === 0 && currentQty > 0) {
					  return (
						<div className="stock-badge stock-badge-max">
						  Max
						</div>
					  );
					}
					
					// Badge orange si stock faible (≤ 3)
					if (stockRemaining > 0 && stockRemaining <= 3 && currentQty > 0) {
					  return (
						<div className="stock-badge stock-badge-low">
						  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
							<path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
						  </svg>
						  {stockRemaining}
						</div>
					  );
					}
					
					return null;
				  })()}
				  
				  {/* ✅ NOUVEAU : Toast au-dessus du produit */}
				  {stockToast.show && stockToast.productId === product.id && (
					<div className="stock-toast">
					  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
						<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
					  </svg>
					  <span>{stockToast.message}</span>
					  <div className="stock-toast-subtitle">Tous les stocks disponibles sont déjà dans votre casier</div>
					</div>
				  )}
				</div>
                
                {/* ✅ MODIFICATION GRID : Éléments en enfants directs pour le Grid layout */}
                <h3 className="product-name">{product.displayName}</h3>
                
                {/* Badge conditionnement - ICÔNES SUPPRIMÉES */}
                <div className="product-packaging-badge">
                  {product.conditionnement === 'canette' && (
                    <span className="packaging-icon canette-icon">
                      Canette
                    </span>
                  )}
                  {product.conditionnement === 'bidon' && (
                    <span className="packaging-icon bidon-icon">
                      Bidon
                    </span>
                  )}
                  {product.conditionnement === 'bouteille_plastique' && (
                    <span className="packaging-icon plastique-icon">
                      Plastique
                    </span>
                  )}
                  {product.conditionnement === 'bouteille_verre' && (
                    <span className="packaging-icon verre-icon">
                      Verre
                    </span>
                  )}
                </div>
                
                <div className="product-packaging">{product.packaging}</div>
                <div className="product-price">{product.price ? product.price.toLocaleString('fr-FR') : '0'} F</div>
                
                <div className="product-actions">
				  <div className="quantity-controls">
					<button 
					  className="quantity-btn quantity-btn-decrease"
					  onClick={() => {
						const currentQuantity = getItemQuantity(product.id);
						if (currentQuantity > 0) {
						  removeFromCart(product.id);
						}
					  }}
					  aria-label="Diminuer la quantité"
					  disabled={getItemQuantity(product.id) === 0}
					>
					  -
					</button>
					<span className="quantity">{getItemQuantity(product.id)}</span>
					<button 
					  className={`quantity-btn quantity-btn-increase ${
						getItemQuantity(product.id) >= product.stock ? 'disabled' : ''
					  }`}
					  onClick={() => handleAddToCart(product)}
					  aria-label="Augmenter la quantité"
					  disabled={getItemQuantity(product.id) >= product.stock}
					>
					  +
					</button>
				  </div>
				</div>
              </div>
            ))}
          </div>
        )}
      </div>
	  
	  {/* ✅ NOUVELLE MODAL AVEC ANIMATIONS ET GESTION BIDONS */}
		{selectedProduct && (
		  <div 
			className={`product-modal-overlay ${isModalOpen ? 'active' : ''}`}
			onClick={closeModal}
		  >
			<div 
			  className={`product-modal-dual ${isModalOpen ? 'active' : ''}`}
			  onClick={(e) => e.stopPropagation()}
			>
			  <button 
				className="modal-close-btn"
				onClick={closeModal}
				aria-label="Fermer"
			  >
				<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
				  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
				</svg>
			  </button>

			  <div className="modal-content-dual">
				{/* ✅ Vérifier si c'est un produit en unité (bidon) */}
				{ImageService.isUnitProduct(selectedProduct) ? (
				  // Pour les bidons : afficher seulement l'image du bidon (centrée)
				  <div className="modal-single-image">
					<div className="modal-image-main modal-bottle-animate">
					  <img 
						src={ImageService.getBottleImageUrl(
						  selectedProduct.code_produit || 
						  selectedProduct.code || 
						  ''
						)}
						alt={selectedProduct.displayName}
						className="modal-product-image-large"
						onError={(e) => {
						  e.target.src = selectedProduct.displayImage || 
										selectedProduct.thumbnailImage || 
										ImageService.getPlaceholderUrl();
						}}
					  />
					</div>
				  </div>
				) : (
				  // Pour les packs : afficher bouteille + pack
				  <div className="modal-images-container">
					{/* ✅ Colonne gauche : Image de la bouteille (GRANDE) */}
					<div className="modal-image-main modal-bottle-animate">
					  <img 
						src={ImageService.getBottleImageUrl(
						  selectedProduct.code_produit || 
						  selectedProduct.code || 
						  ''
						)}
						alt={`${selectedProduct.displayName} - Bouteille`}
						className="modal-product-image-large"
						onError={(e) => {
						  e.target.src = selectedProduct.displayImage || 
										selectedProduct.thumbnailImage || 
										ImageService.getPlaceholderUrl();
						}}
					  />
					</div>

					{/* ✅ Colonne droite : Image du pack (PETITE) */}
					<div className="modal-image-secondary modal-pack-animate">
					  <img 
						src={selectedProduct.packImage || ImageService.getPackImageUrl(
						  selectedProduct.code_produit || 
						  selectedProduct.code || 
						  ''
						)}
						alt={`${selectedProduct.displayName} - Pack`}
						className="modal-product-image-small"
						onError={(e) => {
						  e.target.src = selectedProduct.displayImage || 
										selectedProduct.thumbnailImage || 
										ImageService.getPlaceholderUrl();
						}}
					  />
					</div>
				  </div>
				)}

				{/* Bande d'informations en bas */}
				<div className="modal-info-band">
				  <h2 className="modal-product-name">{selectedProduct.displayName}</h2>
				  
				  {/* Badge conditionnement */}
				  {selectedProduct.conditionnement === 'canette' && (
					<div className="modal-packaging-badge canette">
					  Carton de {selectedProduct.bouteilles_par_pack || 24} canettes
					</div>
				  )}
				  {selectedProduct.conditionnement === 'bouteille_verre' && (
					<div className="modal-packaging-badge verre">
					  Pack de {selectedProduct.bouteilles_par_pack || 6} bouteilles en verre
					</div>
				  )}
				  {selectedProduct.conditionnement === 'bidon' && (
					<div className="modal-packaging-badge bidon">
					  {selectedProduct.bouteilles_par_pack || 1} bidon(s)
					</div>
				  )}
				  {selectedProduct.conditionnement === 'bouteille_plastique' && (
					<div className="modal-packaging-badge plastique">
					  Pack de {selectedProduct.bouteilles_par_pack || 6} bouteilles
					</div>
				  )}
				  
				  <div className="modal-product-price">
					{selectedProduct.price.toLocaleString('fr-FR')} F / {
					  ImageService.isUnitProduct(selectedProduct) 
						? 'unité' 
						: (selectedProduct.conditionnement === 'canette' ? 'carton' : 'pack')
					}
				  </div>
				  
				  <div className="modal-description-section">
					<h3 className="modal-description-title">Description</h3>
					<p className="modal-description-text">
					  {selectedProduct.description || 
					   `${selectedProduct.displayName} est une boisson de qualité supérieure, 
					   parfaite pour se rafraîchir à tout moment de la journée. 
					   ${ImageService.isUnitProduct(selectedProduct) 
						  ? 'Vendu à l\'unité pour votre commodité.' 
						  : `Vendue en ${selectedProduct.conditionnement === 'canette' ? 'carton' : 'pack'} de ${selectedProduct.bouteilles_par_pack || 6} ${selectedProduct.conditionnement === 'canette' ? 'canettes' : 'bouteilles'}.`}
					   Elle offre une expérience de dégustation unique grâce à sa fraîcheur 
					   et son goût authentique.`}
					</p>
				  </div>
				</div>
			  </div>
			</div>
		  </div>
		)}

      {/* Panier flottant (visible uniquement si non vide) */}
      {getTotalItems() > 0 && !isMinimumReached() && (
		  <div className="floating-cart">
			<div className="cart-summary">
			  <div className="minimum-order-warning-inline">
				<svg className="warning-icon-inline">...</svg>
				<svg className="warning-icon-inline" viewBox="0 0 24 24" fill="currentColor">
				  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
				</svg>
				<span className="warning-text-inline">
				  Il manque {getRemainingAmount().toLocaleString('fr-FR')} F pour atteindre le montant minimal
				</span>
			  </div>
			</div>
		  </div>
		)}
    </div>
  );
};

export default ProductListPage;